'use client';

import { SwitchInput } from '@app/app/_components/Input/SwitchInput';
import { TestChoiceButtonInput, TestChoiceButtonOption } from '@app/app/_components/Input/TestChoiceButtonInput';
import { ChangeEvent, ReactNode, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

interface Props {
    name: string;
    options: TestChoiceButtonOption[];
    title?: string;
    additionalContent?: ReactNode;
    switchName?: string;
    switchDefaultChecked?: boolean;
    switchDisabled?: boolean;
    value?: string;
    defaultValue?: string;
    className?: string;
    onChange?: (value: string) => void;
    onSwitchChange?: (checked: boolean) => void;
    disabled?: boolean;
    footerContent?: ReactNode;
}

export function SwitchChoiceButtonInput({
    name,
    options,
    title,
    additionalContent,
    switchName,
    switchDefaultChecked = false,
    switchDisabled = false,
    value,
    defaultValue,
    className,
    onChange,
    onSwitchChange,
    disabled = false,
    footerContent,
}: Props) {
    const handleSwitchChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            onSwitchChange?.(e.target.checked);
        },
        [onSwitchChange],
    );

    const handleChoiceChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            onChange?.(e.target.value);
        },
        [onChange],
    );

    return (
        <div className={twMerge('rounded-lg border border-neutral-200 bg-white p-6', className)}>
            <div className="mb-6 flex items-start justify-between border-b border-neutral-150 pb-8">
                <div className="flex flex-col gap-2">
                    {title && <h3 className="text-lg">{title}</h3>}
                    {additionalContent}
                </div>
                {switchName && (
                    <SwitchInput
                        name={switchName}
                        defaultChecked={switchDefaultChecked}
                        onChange={handleSwitchChange}
                        disabled={switchDisabled}
                    />
                )}
            </div>

            <div className={twMerge(disabled && 'pointer-events-none opacity-50')}>
                <TestChoiceButtonInput
                    name={name}
                    options={options}
                    type="radio"
                    variant="default"
                    onChange={handleChoiceChange}
                    value={value}
                    defaultChecked={defaultValue}
                />
            </div>

            {footerContent && <div className="pt-6">{footerContent}</div>}
        </div>
    );
}
